import React from 'react';

import { Main, MainHeader, Body } from 'components/Main';
import { Panel, PanelTop } from 'components/Panel';
import PlayerLink from 'components/PlayerLink';
import { Table, Th, Td } from 'components/Table';
import { dp, howoutClass, totalScore, runs } from 'components/util';

const ResultPage = ({ result, batting, oppoBatting, bowling, club }) => {
    let clubRolloverDirection = 'right';
    let oppoRolloverDirection = 'left';
    if (result.batted_first === 'No') {
        clubRolloverDirection = 'left';
        oppoRolloverDirection = 'right';
    }
    const clubInnings = (
        <Panel className="cc-batting-innings" border>
            <PanelTop color>
                <h2 className="cc-left">{club.club}</h2>
                <h2 className="cc-right">{totalScore(result.runs, result.wickets)}</h2>
                <div className="cc-clear" />
            </PanelTop>
            <BattingTable
                batting={batting}
                extras={result.extras}
                wickets={result.wickets}
                runs={result.runs}
                rolloverDirection={clubRolloverDirection}
            />
        </Panel>
    );
    const oppoInnings = (
        <Panel className="cc-batting-innings" border>
            <PanelTop color>
                <h2 className="cc-left">{result.opposition}</h2>
                <h2 className="cc-right">{totalScore(result.oppo_runs, result.oppo_wickets)}</h2>
                <div className="cc-clear" />
            </PanelTop>
            <BattingTable
                batting={oppoBatting}
                extras={result.oppo_extras}
                wickets={result.oppo_wickets}
                runs={result.oppo_runs}
                rolloverDirection={oppoRolloverDirection}
                oppo
            />
        </Panel>
    );
    let bothBatting;
    if (result.batted_first === 'Yes') {
        bothBatting = (
            <div className="cc-panel-invisible">
                {clubInnings}
                {oppoInnings}
            </div>
        );
    } else {
        bothBatting = (
            <div className="cc-panel-invisible">
                {oppoInnings}
                {clubInnings}
            </div>
        );
    }
    const resultClass = `cc-result-${result.result.toLowerCase().replace(' ', '_')}`;
    let details = result.details;
    if (result.result === 'Win' || result.result === 'Loss') {
        details = `${club.club} ${result.details.toLowerCase()}`;
    }
    return (
        <Main>
            <MainHeader />
            <Body id="result-page">
                <Panel id="result-page-info">
                    <PanelTop textCenter>
                        <h1>
                            {club.club} {result.lname}
                            <div className="cc-text-weak">v</div>
                            {result.opposition}
                        </h1>
                        <p>
                            {result.location} on {result.long_date} ({result.type})
                        </p>
                        <h2 className={resultClass}>{details}</h2>
                    </PanelTop>
                </Panel>
                {bothBatting}
                <div className="cc-clear" />
                <Panel border>
                    <PanelTop color>
                        <h2>{club.club} Bowling</h2>
                    </PanelTop>
                    <BowlingTable bowling={bowling} />
                </Panel>
            </Body>
        </Main>
    );
};

export default ResultPage;

const BattingTable = ({ batting, extras, wickets, runs, rolloverDirection, oppo }) => {
    const rowClass = oppo ? OppoBattingRow : BattingRow;
    const rows = [];
    let i = 0;
    for (let n = 1; n < 12; n += 1) {
        if (i < batting.length) {
            if (batting[i].num === n) {
                rows.push(React.createElement(rowClass, { key: n, row: batting[i], rolloverDirection }));
                i += 1;
                continue;
            }
        }
        rows.push(
            <tr key={n}>
                <Td className="cc-text-weak">{n}</Td>
                <Td />
                <Td className="cc-1l" />
                <Td className="cc-1l" />
                <Td />
            </tr>
        );
    }
    return (
        <Table striped hover>
            <thead>
                <tr>
                    <Th min="8" />
                    <Th left>Name</Th>
                    <Th className="cc-1l" left />
                    <Th className="cc-1l" left />
                    <Th>Runs</Th>
                </tr>
            </thead>
            <tfoot>
                <tr>
                    <Td min="8" />
                    <Td className="cc-1l" />
                    <Td className="cc-1l" />
                    <Td>Extras</Td>
                    <Td>{extras}</Td>
                </tr>
                <tr>
                    <Td min="8" />
                    <Td className="cc-1l" />
                    <Td className="cc-1l" />
                    <Td>Total ({wickets} wickets)</Td>
                    <Td>{runs}</Td>
                </tr>
            </tfoot>
            <tbody>{rows}</tbody>
        </Table>
    );
};

function getHowout(type, who_player, bowler_player) {
    let howout;
    let bowler;
    if (bowler_player) {
        bowler = <span>b {bowler_player}</span>;
    } else {
        bowler = <span>bowled</span>;
    }
    switch (type) {
        case 'Bowled':
            break;
        case 'LBW':
            howout = 'lbw';
            break;
        case 'Caught':
            if (who_player) {
                howout = <span>c {who_player}</span>;
            } else {
                howout = <span>caught</span>;
            }
            break;
        case 'Caught & Bowled':
            if (bowler_player) {
                bowler = <span>c &amp; b {bowler_player}</span>;
            } else {
                bowler = <span>caught &amp; bowled</span>;
            }
            break;
        case 'Run Out':
            if (who_player) {
                howout = <span>run out ({who_player})</span>;
            } else {
                howout = <span>run out</span>;
            }
            bowler = undefined;
            break;
        case 'Stumped':
            if (who_player) {
                howout = <span>st {who_player}</span>;
            } else {
                howout = <span>stumped</span>;
            }
            break;
        case 'DNB':
            howout = <span>dnb</span>;
            bowler = undefined;
            break;
        case 'Hit Wicket':
            howout = <span>hit wicket</span>;
            break;
        case 'Retired':
            howout = <span>retired</span>;
            bowler = undefined;
            break;
        case 'NOT OUT':
        default:
            howout = type;
            bowler = undefined;
            break;
    }
    return {
        howout,
        bowler
    };
}

const BattingRow = ({ row, rolloverDirection }) => {
    let who_player;
    if (row.who) {
        who_player = <span className="cc-player-oppo">{row.who}</span>;
    }
    let bowler_player;
    if (row.bowler) {
        bowler_player = <span className="cc-player-oppo">{row.bowler}</span>;
    }
    const howout = getHowout(row.howout, who_player, bowler_player);
    const howoutClassName = howoutClass(row.howout);
    return (
        <tr>
            <Td min="8" className="cc-text-weak">
                {row.num}
            </Td>
            <Td left>
                <PlayerLink pid={row.pid} fname={row.fname} lname={row.lname} runs={row.runs} rolloverDirection={rolloverDirection} />
                <div className={`cc-text-small cc-2l ${howoutClassName}`} left>
                    {howout.howout} {howout.bowler}
                </div>
            </Td>
            <Td className={`cc-1l ${howoutClassName}`} left>
                {howout.howout}
            </Td>
            <Td className={`cc-1l ${howoutClassName}`} left>
                {howout.bowler}
            </Td>
            <Td>{runs(row.runs, row.howout)}</Td>
        </tr>
    );
};

const OppoBattingRow = ({ row, rolloverDirection }) => {
    let who_player;
    if (row.who_pid) {
        who_player = <PlayerLink pid={row.who_pid} fname={row.who_fname} lname={row.who_lname} rolloverDirection={rolloverDirection} />;
    }
    let bowler_player;
    if (row.bowler_pid) {
        bowler_player = (
            <PlayerLink pid={row.bowler_pid} fname={row.bowler_fname} lname={row.bowler_lname} rolloverDirection={rolloverDirection} />
        );
    }
    const howout = getHowout(row.howout, who_player, bowler_player);
    const howoutClassName = howoutClass(row.howout);
    return (
        <tr>
            <Td min="8" className="cc-text-weak">
                {row.num}
            </Td>
            <Td left>
                {row.name}
                <div className={`cc-text-small cc-2l ${howoutClassName}`} left>
                    {howout.howout} {howout.bowler}
                </div>
            </Td>
            <Td className={`cc-1l ${howoutClassName}`} left>
                {howout.howout}
            </Td>
            <Td className={`cc-1l ${howoutClassName}`} left>
                {howout.bowler}
            </Td>
            <Td>{runs(row.runs, row.howout)}</Td>
        </tr>
    );
};

const BowlingTable = ({ bowling }) => (
    <Table striped hover>
        <thead>
            <tr>
                <Th left>Name</Th>
                <Th short="3" value="O">
                    Overs
                </Th>
                <Th short="3" value="M">
                    Maidens
                </Th>
                <Th short="3" value="R">
                    Runs
                </Th>
                <Th short="3" value="W">
                    Wickets
                </Th>
                <Th short="3" value="Avg">
                    Average
                </Th>
                <Th priority="2" short="3" value="S/R">
                    Strike Rate
                </Th>
                <Th priority="2" short="3" value="E/R">
                    Economy
                </Th>
            </tr>
        </thead>
        <tbody>{bowling.map(row => <BowlingRow key={row.num} row={row} />)}</tbody>
    </Table>
);

const BowlingRow = ({ row }) => (
    <tr>
        <Td left>
            <PlayerLink pid={row.pid} fname={row.fname} lname={row.lname} wickets={row.wickets} />
        </Td>
        <Td>{row.overs}</Td>
        <Td>{row.maidens}</Td>
        <Td>{row.runs}</Td>
        <Td>{row.wickets}</Td>
        <Td>{dp(row.average)}</Td>
        <Td priority="2">{dp(row.strike)}</Td>
        <Td priority="2">{dp(row.economy)}</Td>
    </tr>
);



// WEBPACK FOOTER //
// ./src/views/result/ResultPage.jsx